// Canal calculé à partir de deux modalités de fluorescence très semblables, pixel par pixel, après normalisation de
// chacune par son contraste (min / max de sa carte) et, si sa carte est inversée, inversion (1 − valeur : en
// transmission, le tissu absorbant devient clair sur fond sombre, comme une fluorescence) :
// - différence : A − B ;
// - rapport : log2(A / B) (A deux fois plus intense que B ou l'inverse : même intensité de couleur), modulé par
//   la luminosité moyenne.
// A > B en couleur A (magenta), B > A en couleur B (vert), noir là où elles sont égales ; un gain amplifie, un fond
// gris rappelle la structure commune. Inversé : fond blanc (égales en blanc, structure commune en gris), mêmes couleurs
// pour A et B (inverser simplement les couleurs échangerait magenta et vert).
// Équilibre : B multiplié par 2^balance avant la comparaison, pour compenser un écart global d'intensité entre les deux
// images (sinon tout penche du même côté) ; autoBalance le calcule pour que A et B soient égales en médiane sur le tissu.
// Masque : pixels dont l'intensité normalisée de A, de B ou de leur moyenne est sous le seuil, laissés sans couleur
// (un rapport de deux bruits n'a pas de sens) ; l'aperçu montre en blanc les pixels gardés.
import { VivLayerExtension, XRLayer } from '@hms-dbmi/viv';

import { pixelSize } from '../../services';

export const DIFF_COLORS = { a: [255, 0, 255], b: [0, 255, 0] };
export const DIFF_MODES = { diff: 0, ratio: 1 };
export const MASK_SOURCES = { mean: 0, a: 1, b: 2 };
export const DERIVED_ID = 'derived';  // identifiant du canal calculé parmi les modalités
export const DERIVED_DEFAULTS = {
  op: 'ratio', gain: 4, balance: 0, smooth: 1, context: 0, mask: 'mean', threshold: 0.05, inverted: false,
};

const diffModule = {
  name: 'diffModule',
  uniformTypes: {
    gain: 'f32', balance: 'f32', context: 'f32', mode: 'f32', maskSource: 'f32', maskThreshold: 'f32', maskPreview: 'f32', inverted: 'f32',
    invertA: 'f32', invertB: 'f32', colorA: 'vec3<f32>', colorB: 'vec3<f32>',
  },
  fs: `
uniform diffModuleUniforms {
  float gain;
  float balance;
  float context;
  float mode;
  float maskSource;
  float maskThreshold;
  float maskPreview;
  float inverted;
  float invertA;
  float invertB;
  vec3 colorA;
  vec3 colorB;
} diffModule;

vec3 diff_color(float[NUM_CHANNELS] intensity) {
  float a = clamp(intensity[0], 0., 1.);
  float b = clamp(intensity[1], 0., 1.);
  if (diffModule.invertA > 0.5) a = 1. - a;  // carte de A inversée
  if (diffModule.invertB > 0.5) b = 1. - b;
  float masked = diffModule.maskSource < 0.5 ? 0.5 * (a + b) : (diffModule.maskSource < 1.5 ? a : b);
  float keep = step(diffModule.maskThreshold, masked);
  if (diffModule.maskPreview > 0.5) return vec3(keep);
  float bb = b * exp2(diffModule.balance);  // B équilibrée
  float d;
  if (diffModule.mode < 0.5) {
    d = (a - bb) * diffModule.gain;
  } else {
    float m = 0.5 * (a + bb);
    float eps = 1. / 255.;
    d = log2((a + eps) / (bb + eps)) * diffModule.gain * 0.25;  // × 0,25 : même ordre de grandeur que la différence
    d *= sqrt(m);                                               // couleur modulée par la luminosité
  }
  d *= keep;
  float structure = diffModule.context * 0.5 * (a + b);  // fond gris : structure commune, pour se repérer
  if (diffModule.inverted > 0.5) {
    // fond blanc : on part du blanc et on retire le complément de la couleur (A -> magenta, B -> vert)
    vec3 w = vec3(1.) - max(d, 0.) * (vec3(1.) - diffModule.colorA) - max(-d, 0.) * (vec3(1.) - diffModule.colorB);
    return clamp(w - structure, 0., 1.);
  }
  vec3 c = max(d, 0.) * diffModule.colorA + max(-d, 0.) * diffModule.colorB;
  return clamp(c + structure, 0., 1.);
}`,
  inject: { 'fs:DECKGL_MUTATE_COLOR': 'rgba = vec4(diff_color(intensity), 1.0);' },
};

// Réglages lus dans la prop « derived » du calque : un changement de curseur ne met à jour que les uniformes, sans
// recompiler le shader.
export class DiffExtension extends VivLayerExtension {
  getVivShaderTemplates() { return { modules: [diffModule] }; }

  updateState(params, extension) {
    super.updateState(params, extension);
    const d = { ...DERIVED_DEFAULTS, ...(this.props.derived || {}) };
    const uniforms = {
      gain: d.gain,
      balance: d.balance,
      context: d.context,
      mode: DIFF_MODES[d.op] ?? DIFF_MODES.ratio,
      maskSource: MASK_SOURCES[d.mask] ?? MASK_SOURCES.mean,
      maskThreshold: d.threshold,
      maskPreview: d.maskPreview ? 1 : 0,
      inverted: d.inverted ? 1 : 0,
      invertA: d.invertA ? 1 : 0,
      invertB: d.invertB ? 1 : 0,
      colorA: DIFF_COLORS.a.map(v => v / 255),
      colorB: DIFF_COLORS.b.map(v => v / 255),
    };
    for (const model of this.getModels()) model.shaderInputs.setProps({ diffModule: uniforms });
  }
}
DiffExtension.extensionName = 'DiffExtension';

// Source Viv à deux canaux : canal 0 = image A, canal 1 = image B, lues tuile par tuile dans leurs propres sources.
class PairSource {
  constructor(a, b) {
    const { width, height } = pixelSize(a);
    this.a = a;
    this.b = b;
    this.shape = [1, 2, 1, height, width];
    this.labels = ['t', 'c', 'z', 'y', 'x'];
    this.dtype = a.dtype;
    this.tileSize = a.tileSize;
    this.meta = {};
  }

  getTile({ x, y, selection, signal }) {
    const source = selection.c === 1 ? this.b : this.a;
    return source.getTile({ x, y, selection: { t: 0, z: 0, c: 0 }, signal });
  }

  onTileError(err) {
    console.error(err);
  }
}

// Pyramide à deux canaux, ou message expliquant pourquoi les deux images ne sont pas comparables pixel à pixel.
export function pairLoader(loaderA, loaderB) {
  const [a, b] = [loaderA[0], loaderB[0]];
  const sa = pixelSize(a);
  const sb = pixelSize(b);
  if (sa.width !== sb.width || sa.height !== sb.height || loaderA.length !== loaderB.length) {
    return { error: 'Different dimensions: both images must be on the same pixel grid (scans rewritten on a common grid, see tools/registration/auto).' };
  }
  if (a.tileSize !== b.tileSize || a.dtype !== b.dtype) {
    return { error: 'Different formats: prepare the display (tiles) of both images.' };
  }
  return { loader: loaderA.map((level, k) => new PairSource(level, loaderB[k])) };
}

// Équilibre automatique (log2 du facteur appliqué à B) : médiane de log2(A / B) sur les pixels gardés par le masque,
// les deux images normalisées par leur contraste (et inversées comme dans le shader : settings.invertA / invertB), sur
// un niveau réduit d'au moins 200 000 pixels ; borné à [-2, 2].
export async function autoBalance(loaderA, loaderB, contrastA, contrastB, settings) {
  let level = loaderA.length - 1;
  while (level > 0) {
    const { width, height } = pixelSize(loaderA[level]);
    if (width * height >= 2e5) break;
    level -= 1;
  }
  const selection = { t: 0, z: 0, c: 0 };
  const [ra, rb] = await Promise.all([loaderA[level].getRaster({ selection }), loaderB[level].getRaster({ selection })]);
  const s = { ...DERIVED_DEFAULTS, ...settings };
  const norm = ([lo, hi], inverted) => v => {
    const n = Math.min(1, Math.max(0, (v - lo) / ((hi - lo) || 1)));
    return inverted ? 1 - n : n;
  };
  const na = norm(contrastA, s.invertA);
  const nb = norm(contrastB, s.invertB);
  const eps = 1 / 255;
  const logs = [];
  for (let i = 0; i < ra.data.length; i += 1) {
    const a = na(ra.data[i]);
    const b = nb(rb.data[i]);
    const masked = s.mask === 'a' ? a : s.mask === 'b' ? b : 0.5 * (a + b);
    if (masked >= s.threshold) logs.push(Math.log2((a + eps) / (b + eps)));
  }
  if (!logs.length) return null;
  logs.sort((x, y) => x - y);
  return Math.max(-2, Math.min(2, logs[Math.floor(logs.length / 2)]));
}

// Tuiles du canal calculé toujours interpolées (linéaire), y compris au-delà de la pleine résolution, où Viv affiche les
// pixels en blocs (« nearest ») : un rapport amplifié en pixels carrés paraît crénelé. Sinon identique au rendu de Viv.
export function renderLinearTile(props) {
  const { bbox: { left, top }, index: { x, y, z } } = props.tile;
  const { data, id } = props;
  if ([left, top].some(v => v < 0) || !data || !data.width || !data.height) return null;
  const scale = 2 ** Math.round(-z);
  const bounds = [left, top + data.height * scale, left + data.width * scale, top];
  return new XRLayer(props, {
    channelData: data, bounds, id: `tile-sub-layer-${bounds}-${id}`, tileId: { x, y, z }, interpolation: 'linear',
  });
}
